import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';
import { useMediaQuery } from '@mui/material';

// Create the context
const ThemeContext = createContext(null);

// Custom hook to use the theme context
export const useThemePreferences = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemePreferences must be used within a ThemeProvider');
  }
  return context;
};

// Provider component
export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [unitPreference, setUnitPreference] = useState(localStorage.getItem('unitPreference') || 'imperial');
  
  // Function to toggle dark mode
  const toggleDarkMode = useCallback(() => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode);
  }, [darkMode]);
  
  // Function to toggle unit preference
  const toggleUnitPreference = useCallback(() => {
    const newUnitPreference = unitPreference === 'imperial' ? 'metric' : 'imperial';
    setUnitPreference(newUnitPreference);
    localStorage.setItem('unitPreference', newUnitPreference);
  }, [unitPreference]);
  
  // Create the base theme
  const baseTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? 'dark' : 'light',
          background: {
            default: darkMode ? '#121212' : '#ffffff',
            paper: darkMode ? '#1e1e1e' : '#ffffff'
          }
        }
      }),
    [darkMode]
  );
  
  // Check if we're on mobile
  const mobile = useMediaQuery(baseTheme.breakpoints.down('sm'));
  
  // Create the fully configured theme with responsive values
  const theme = useMemo(() => {
    return createTheme({
      ...baseTheme,
      typography: {
        ...baseTheme.typography,
        body1: {
          ...baseTheme.typography.body1,
          fontSize: mobile ? '1rem' : '0.875rem'
        },
        body2: {
          ...baseTheme.typography.body2,
          fontSize: mobile ? '0.875rem' : '0.75rem'
        }
      },
      components: {
        ...baseTheme.components,
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: mobile ? 8 : 4,
              textTransform: 'none',
              fontWeight: 500
            }
          }
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: mobile ? 12 : 8
            }
          }
        },
        MuiListItem: {
          styleOverrides: {
            root: {
              paddingTop: mobile ? 10 : 6,
              paddingBottom: mobile ? 10 : 6
            }
          }
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              padding: mobile ? 10 : 8
            }
          }
        }
      }
    });
  }, [baseTheme, mobile]);
  
  // Set up viewport height variable for mobile
  useEffect(() => {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    const resizeHandler = () => {
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);
  
  // Disable scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  
  // Create the context value object
  const themeContextValue = {
    darkMode,
    unitPreference,
    toggleDarkMode,
    toggleUnitPreference,
    isMobile: mobile
  };
  
  // Provide the context value to children
  return (
    <ThemeContext.Provider value={themeContextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 